import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useMediaQuery, Spinner, Flex, Button } from "@chakra-ui/react";
import { Column } from "react-table";

import api from "services/api";
import { useAuth } from "hooks/useAuth";
import { characterDelimiter } from "utils/characterDelimiter";
import { InputReactMask } from "components/InputReactMask";
import { useCustomToast } from "hooks/useToast";
import { Table } from "./Table";
import { RenderRowProducts } from "./RenderRowProducts";

const PAGE_SIZE = 20;

export const TableTransaction = () => {
  const { dataUser } = useAuth();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [shouldFetch, setShouldFetch] = useState(false); // Novo estado para controlar a requisição
  const { getToast } = useCustomToast();
  const [isMobileSize] = useMediaQuery("(max-width: 992px)");

  const formatDateOnly = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };


  const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    const dia = d.getDate().toString().padStart(2, "0");
    const mes = (d.getMonth() + 1).toString().padStart(2, "0");
    const ano = d.getFullYear();
    return `${dia}/${mes}/${ano}`;
  };

  const fetchTransactions = useCallback(async (page: number, dataInicioStr: string, dataFimStr: string) => {
    setLoading(true);
    try {
      const [d, m, y] = dataInicioStr.split("/");
      const [d2, m2, y2] = dataFimStr.split("/");
      const formatStart = new Date(Number(y), Number(m) - 1, Number(d));
      const formatEnd = new Date(Number(y2), Number(m2) - 1, Number(d2));

      const payload = {
        dataDe: formatDateOnly(formatStart),
        dataAte: formatDateOnly(formatEnd),
      };

      const { data } = await api.post("/motorDePontos/resgate/busca", payload);

      setTransactions(data || []);
    } catch (error) {
      console.error(error);
      getToast({
        messageError: "Erro ao buscar resgates.",
        status: "error",
      });
      setTransactions([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [dataUser.documento, getToast]);

  // UseEffect para definir as datas iniciais na montagem inicial
  useEffect(() => {
    const hoje = new Date();
    const inicio = new Date();
    inicio.setDate(hoje.getDate() - 29);

    setDataInicio(formatDate(inicio));
    setDataFim(formatDate(hoje));
    setShouldFetch(true); // Dispara a primeira busca
  }, []);

  // UseEffect que agora depende apenas de currentPage e shouldFetch
  useEffect(() => {
    if (shouldFetch && dataInicio && dataFim) {
      fetchTransactions(currentPage, dataInicio, dataFim);
      setShouldFetch(false); // Reseta o estado para evitar loops
    }
  }, [currentPage, shouldFetch, dataInicio, dataFim, fetchTransactions]);

  const filtrar = () => {
    if (!dataInicio || !dataFim) {
      getToast({
        messageError: "Por favor, preencha ambas as datas.",
        status: "error",
      });
      return;
    }

    const [d, m, y] = dataInicio.split("/");
    const [d2, m2, y2] = dataFim.split("/");
    const formatStart = new Date(Number(y), Number(m) - 1, Number(d));
    const formatEnd = new Date(Number(y2), Number(m2) - 1, Number(d2));

    if (formatStart > formatEnd) {
      getToast({
        messageError: "A data inicial não pode ser maior que a data final.",
        status: "error",
      });
      return;
    }

    const now = new Date();
    if (formatEnd > now) {
      getToast({
        messageError: "A data final não pode ser maior que a data atual.",
        status: "error",
      });
      return;
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      setShouldFetch(true);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setShouldFetch(true);
  };

  /*
   "resgateId": 40,
        "pontos": 70.00,
        "valor": 49.00,
        "chavePix": "edison@nts.com.br",
        "dataResgate": "2026-02-25",
        "nomeTitular": "EDISON LUIZ GONCALEZ",
        "bancoDestino": "PICPAY INSTITUIçãO DE PAGAMENTO S.A.",
        "status": "Aceito",
        "motivoRecusa": null
  */

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: "Data",
        accessor: "dataResgate",
        Cell: ({ value }: { value: any }) => <span>{formatDate(value)}</span>,
      },
      {
        Header: "Chave",
        accessor: "chavePix",
        Cell: ({ value }: { value: any }) => <span>{characterDelimiter(value, 45)}</span>,
      },
      {
        Header: "Nome",
        accessor: "nomeTitular",
        Cell: ({ value }: { value: any }) => <span>{characterDelimiter(value, 45)}</span>,
      },
      {
        Header: "status",
        accessor: "status",
        Cell: ({ value }: { value: any }) => <span>{characterDelimiter(value, 45)}</span>,
      },
      {
        Header: "Valor",
        accessor: "valor",
        isNumeric: true,
        id: "valor",
      },
    ],
    []
  );

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <div style={{ width: isMobileSize ? 110 : 200 }}>
          <InputReactMask
            mask="99/99/9999"
            nameInput="dataInicio"
            label="Data inicial:"
            placeholder="00/00/0000"
            width={200}
            isDate
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDataInicio(e.target.value)}
            value={dataInicio}
          />
        </div>
        <div style={{ width: isMobileSize ? 10 : 30 }} />
        <div style={{ width: isMobileSize ? 110 : 200 }}>
          <InputReactMask
            mask="99/99/9999"
            nameInput="dataFim"
            label="Data final:"
            placeholder="00/00/0000"
            width={200}
            isDate
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDataFim(e.target.value)}
            value={dataFim}
          />
        </div>
        <div style={{ width: isMobileSize ? 10 : 30 }} />
        <Button
          onClick={filtrar}
          height="40px"
          backgroundColor="#009D57"
          fontSize={isMobileSize ? "14px" : "18px"}
          color="#FFFF"
          marginTop={isMobileSize ? 2 : 6}
        >
          Pesquisar
        </Button>
      </div>
      <br />
      {loading ? (
        <Flex paddingBottom={20} justifyContent="center">
          <Spinner color="#009D57" />
        </Flex>
      ) : (
        <>
          <div
            style={{
              overflowX: "visible",
              overflowY: "visible",
            }}
          >
            <Table
              columns={columns}
              data={transactions ?? []}
              isExpanded={false}
              renderRowSubComponent={RenderRowProducts}
            />
          </div>
          {transactions.length === 0 && (
            <span
              style={{ fontSize: 20, color: "#10125A", fontWeight: 500, marginTop: -40, marginBottom: 40 }}
            >
              Não foram encontrados registros
            </span>
          )}
          {totalPages > 1 && (
            <Flex justifyContent="center" alignItems="center" mt={4} marginBottom={12}>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  marginRight: 8,
                  padding: "4px 12px",
                  borderRadius: 4,
                  background: "#009D57",
                  color: "white",
                  border: 0,
                }}
              >
                Anterior
              </button>
              <span style={{ margin: "0 8px", fontSize: isMobileSize ? "18px" : "26px" }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  marginLeft: 8,
                  padding: "4px 12px",
                  borderRadius: 4,
                  background: "#009D57",
                  color: "white",
                  border: 0,
                }}
              >
                Próxima
              </button>
            </Flex>
          )}
        </>
      )}
    </>
  );
};